import {TextEncoder} from "encoding";
import Bottleneck from "bottleneck";
import Modbus from "../modbus_with_scheduler.js";
import {SerialPort}	from "webserial";
import * as usbutil from "../usbutil.js";
import Device from "../device.js";

const REG_BHT_MODEL_NUMBER = 0xF110;
const REG_SERIAL_NUMBER = 0xF118;
const REG_MEASURED_VALUE = 0x20;
const REG_SETPOINT = 0x21;
const REG_ALARM_INFO = 0x0034;
const REG_TEMPERATURE = 0x0427;
const REG_TEMPERATURE2 = 0xA138;
const REG_COUNTER_VALUE = 0x0D01;
const REG_WINK = 0x0000;
const REG_CAPACITY_100 = 0x8168;
const REG_CAPACITY_UNIT = 0x81f8;


const REG_FLUID_NAME = 0x8188;

export default class MyDevice extends Device {
  constructor(serialPort_or_modbus) {        
    super(serialPort_or_modbus);

    this.id = "-";
    this.status = "-";        
    this.selectedFluidName = "-";
    this.serialNumber = "-";
    this.value = "-";
    this.targetValue = "-";    

    this.__aerationErrorSum = 0;

    this.physical_layer = serialPort_or_modbus;

    if (serialPort_or_modbus instanceof Modbus) {
      this.modbus = serialPort_or_modbus;
      this.__open = true;
    } else {      
      /* wrap the serialPort object into Modbus if necessary */    
      this.modbus = new Modbus();
      this.modbus.wrap(serialPort_or_modbus);      
    }

    jfermi.addEventListener("fermentationstart", this.fermentationstart_handler.bind(this));
    jfermi.addEventListener("fermentationstop", this.fermentationstop_handler.bind(this));
  }
  async initialize() {
    try {            
      if(!this.__open) {
        await this.modbus.open({
          baudRate: 19200,
          dataBits: 8,
          parity: "none",
          stopBits: 2    
        });      
      }

      this.id = await this.getBHTModelNumber();
      this.serialNumber = await this.getSerialNumber();
      this.selectedFluidName = await this.getSelectedFluidName();
      this.maximalCapacity = await this.getCapacity100();
      this.capacityUnit = await this.getCapacityUnit();           
      this.targetValue = await this.getMLH();

      if (this.physical_layer instanceof SerialPort) {
        this.usbProductName = (await this.physical_layer.getInfo()).usbProductName;
        this.usbSerialNumber = (await this.physical_layer.getInfo()).usbSerialNumber;    
      } else {
        this.usbProductName = "bronkhorst_iqflow";
        this.usbSerialNumber = this.serialNumber;         
      }

      this.name = usbutil.getDeviceName(`${this.usbProductName}_${this.usbSerialNumber}`) || "iqflow";      

      this.status = "OK";

    } catch(e) {
      this.status = `Failed: ${e}`;
      throw e;
    }
  }
  async update() {
    try {                    
      this.value = await this.getCurrentMLM();
      this.status = "Ok";
    } catch(e) {
      this.value = "-";
      this.status = `Failed: ${e}`;
      throw e;
    }      
  }
  async control(fermentation, devices, settings) {
    var DO;
    if (devices.dissolvedOxygenSensor)
      DO = devices.dissolvedOxygenSensor.value;
    if (devices.ezodoSensor)
      DO = devices.ezodoSensor.value;
    if (devices.visifermdo)
      DO = devices.visifermdo.value;

    if (Number.isFinite(fermentation.oxygenLevel) && Number.isFinite(DO)) {      
      let output = 0;

      let error = DO - fermentation.oxygenLevel;
      error *= -1;     

      let aerationErrorSumOld = this.__aerationErrorSum;
      this.__aerationErrorSum += error;

      let Omin = settings.IQFlow_MIN_MLM;
      let Pout = settings.DO2_Kp * error; 									
      let Iout = settings.DO2_Ki * (this.__aerationErrorSum * 3);			

      output = Omin + Pout + Iout;      

      output = Math.min(settings.IQFlow_MAX_MLM, output);
      output = Math.max(settings.IQFlow_MIN_MLM, output);

      if ((settings.IQFlow_MIN_MLM >= output) || (output >= settings.IQFlow_MAX_MLM)) {
        this.__aerationErrorSum = aerationErrorSumOld;
      }

      //localStorage.setItem("__aerationErrorSum", this.__aerationErrorSum);

      await this.setMLM(output);
    }                  
  }
  async fermentationstart_handler() {
    this.__aerationErrorSum = parseFloat(localStorage.getItem("__aerationErrorSum")) || 0;
  }
  async fermentationstop_handler() {
    await this.setMLM(0);
    localStorage.removeItem("__aerationErrorSum");
  }  
  async getBHTModelNumber() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_BHT_MODEL_NUMBER, 7);
    var uint8Array = new Uint8Array(arrayBuffer);

    var string = new TextDecoder().decode(uint8Array);
    return(string);
  }
  async getSerialNumber() {
    /* after the 5th register it reads garbage */
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_SERIAL_NUMBER, 5);
    var uint8Array = new Uint8Array(arrayBuffer);

    var string = new TextDecoder().decode(uint8Array);
    return(string);
  }  
  async getMeasuredValue() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_MEASURED_VALUE, 1);
    var dataView = new DataView(arrayBuffer);    
    return dataView.getUint16(0);
  }
  async getCurrentMLM() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_MEASURED_VALUE, 1);
    var dataView = new DataView(arrayBuffer);    
    var value = dataView.getUint16(0);

    var unit = await this.getCapacityUnit();      
    var maximalCapacity = await this.getCapacity100();
    if (unit=="ls/min") maximalCapacity *= 1000;
    if (unit=="ln/min") maximalCapacity *= 1000;
    var div = 32000/maximalCapacity;

    return Math.round(value/div);
  }  
  async getCounterValue() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_COUNTER_VALUE, 1);
    var dataView = new DataView(arrayBuffer);    
    return dataView.getUint16(0);
  }  
  async getSelectedFluidName() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_FLUID_NAME, 5);
    var uint8Array = new Uint8Array(arrayBuffer);

    var string = new TextDecoder().decode(uint8Array);
    return string;        
  }
  async getAlarmInfo() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_ALARM_INFO, 1);
    var uint8Array = new Uint8Array(arrayBuffer);
    return uint8Array[0];
  }
  async getInternalTemperature() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_TEMPERATURE, 1);

    var dataView = new DataView(arrayBuffer);    
    return dataView.getUint16(0);
  }  
  async getInternalTemperature2() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_TEMPERATURE2, 2);

    var dataView = new DataView(arrayBuffer);    
    return dataView.getFloat32(0);
  }    
  async getCapacity100() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_CAPACITY_100, 2);

    var dataView = new DataView(arrayBuffer);    
    return dataView.getFloat32(0);
  }  
  async getCapacityUnit() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_CAPACITY_UNIT, 4);
    var uint8Array = new Uint8Array(arrayBuffer);
    var string = new TextDecoder().decode(uint8Array);
    string = string.replace(/(\r\n|\n|\r|\0| )/gm, "");    
    return string;
  }
  async setSetPoint(value) {
    var arrayBuffer = new ArrayBuffer(2);
    var dataView = new DataView(arrayBuffer);
    dataView.setUint16(0, value);
    await this.modbus.presetMultipleRegisters(0x1, REG_SETPOINT, arrayBuffer);
    return(true);
  }
  async getSetPoint() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_SETPOINT, 1);

    var dataView = new DataView(arrayBuffer);    
    return dataView.getUint16(0);    
  }  
  async wink() {
    var arrayBuffer = new ArrayBuffer(2);
    var dataView = new DataView(arrayBuffer);
    dataView.setUint16(0, 1);
    await this.modbus.presetMultipleRegisters(0x1, REG_WINK, arrayBuffer);        
  }  
  async setMLM(value) {
    try {       
      var unit = await this.getCapacityUnit();      
      var maximalCapacity = await this.getCapacity100();
      if (unit=="ls/min") maximalCapacity *= 1000;
      if (unit=="ln/min") maximalCapacity *= 1000;
      var mul = 32000/maximalCapacity;          
      await this.setSetPoint(Math.round(value*mul));
      this.status = "Ok";
      this.targetValue = value;
    } catch(e) {
      this.status = `Failed: ${e}`;
      throw e;
    }            
  }  
  async getMLH() {
    var arrayBuffer = await this.modbus.readHoldingRegisters(0x1, REG_SETPOINT, 1);
    var dataView = new DataView(arrayBuffer);    
    var value = dataView.getUint16(0);

    var unit = await this.getCapacityUnit();      
    var maximalCapacity = await this.getCapacity100();
    if (unit=="ls/min") maximalCapacity *= 1000;
    if (unit=="ln/min") maximalCapacity *= 1000;
    var div = 32000/maximalCapacity;

    return Math.round(value/div);    
  }  
  async RM_setMLM(value) {
      await this.setMLM(value);
  }
  async RM_wink() {
    await this.wink();
  }    
  toJSON() {
    return({
      name: this.name,            
      usbProductName: this.usbProductName,
      usbSerialNumber: this.usbSerialNumber,
      status: this.status,      
      id: this.id,
      serialNumber: this.serialNumber,
      value: Math.round(this.value),
      targetValue: Math.round(this.targetValue),
      sp: Math.round(this.targetValue),
      pv: Math.round(this.value),
      maximalCapacity: `${this.maximalCapacity} ${this.capacityUnit}`,
      capacityUnit: this.capacityUnit,
      selectedFluidName: this.selectedFluidName
    });
  }
}